import { useSubmit } from "@certn/api";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
  showToast,
  Text,
} from "@certn/cascadia-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { usePermissions } from "../../../components/permissions/PermissionsProvider";
import {
  useDoesPackageContainDeactivatedTemplate,
  useDuplicatePackage,
} from "../hooks";
import {
  packageCreatePermissions,
  packagePagePermissions,
  packageUpdatePermissions,
} from "../package-permissions";
import { DeactivatePackageModal } from "./DeactivatePackageModal";
import { ReactivationWarningModal } from "./ReactivationWarningModal";

export { packagePagePermissions, packageUpdatePermissions };

interface Props {
  id: string;
  packageName: string;
  isActive: boolean;
  caseCount?: number;
  refreshPackage: () => void;
}

export function ActionsDropdown(props: Props) {
  const { id, packageName, isActive, caseCount = 0, refreshPackage } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packageImprovements } = useFlags();
  const [showReactivationWarningModal, setShowReactivationWarningModal] =
    useState<boolean>(false);
  const [showDeactivationModal, setShowDeactivationModal] =
    useState<boolean>(false);

  const packageContainsDeactivatedTemplate =
    useDoesPackageContainDeactivatedTemplate(id);
  const { duplicatePackage, isDuplicating } = useDuplicatePackage({
    onSuccess: () => refreshPackage(),
  });

  const { submit: submitActivate, isSubmitting: isSubmittingActivate } =
    useSubmit(`/api/internal/client-portal/packages/${id}/activate/`, {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("{{name}} has been reactivated.", { name: packageName }), {
          type: "success",
        });
        refreshPackage();
      },
    });
  const { submit: submitDeactivate, isSubmitting: isSubmittingDeactivate } =
    useSubmit(`/api/internal/client-portal/packages/${id}/deactivate/`, {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("{{name}} has been deactivated.", { name: packageName }), {
          type: "success",
        });
        refreshPackage();
      },
    });
  const { hasPermission } = usePermissions();
  const hasPermissionToDeactivate = hasPermission(packagePagePermissions);
  const hasPermissionToUpdatePackage = hasPermission(packageUpdatePermissions);
  const canEditPackage =
    packageImprovements && caseCount === 0 && hasPermissionToUpdatePackage;
  const showViewPackage =
    !packageImprovements || caseCount > 0 || !hasPermissionToUpdatePackage;

  const disabledKeys = [
    ...(isSubmittingActivate ? ["activate-template"] : []),
    ...(isSubmittingDeactivate ? ["deactivate-template"] : []),
    ...(isDuplicating ? ["duplicate-package"] : []),
  ];

  const handleActivate = () => {
    if (packageContainsDeactivatedTemplate) {
      setShowReactivationWarningModal(true);
    } else {
      submitActivate();
    }
  };

  const navigateToPackage = (viewOnly: boolean) => {
    navigate(viewOnly ? `/package/${id}?view=true` : `/package/${id}`);
  };

  const handleDuplicate = () => {
    void duplicatePackage(id);
  };

  return (
    <>
      <DropdownMenu
        disabledKeys={disabledKeys}
        triggerButton={
          <GhostIconButton
            testID="package-item-actions-button"
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        placement="bottom right"
        onItemAction={(key) => {
          switch (key) {
            case "view-package":
              navigateToPackage(Boolean(packageImprovements));
              break;
            case "edit-package":
              navigateToPackage(false);
              break;
            case "deactivate-package":
              setShowDeactivationModal(true);
              break;
            case "activate-package":
              handleActivate();
              break;
            case "duplicate-package":
              handleDuplicate();
              break;
          }
        }}
      >
        <MenuSection>
          {showViewPackage && (
            <MenuItem id="view-package" testID="view-package">
              <Text style="input/text-field">{t("View package")}</Text>
            </MenuItem>
          )}
          {canEditPackage && (
            <MenuItem id="edit-package" testID="edit-package">
              <Text style="input/text-field">{t("Edit package")}</Text>
            </MenuItem>
          )}
          {packageImprovements && hasPermission(packageCreatePermissions) && (
            <MenuItem id="duplicate-package" testID="duplicate-package">
              <Text style="input/text-field">{t("Duplicate")}</Text>
            </MenuItem>
          )}
          {hasPermissionToDeactivate && isActive && (
            <MenuItem id="deactivate-package" testID="deactivate-package">
              <Text color="text-danger" style="input/text-field">
                {t("Deactivate")}
              </Text>
            </MenuItem>
          )}
          {hasPermissionToDeactivate && !isActive && (
            <MenuItem id="activate-package" testID="activate-package">
              <Text style="input/text-field">{t("Activate")}</Text>
            </MenuItem>
          )}
        </MenuSection>
      </DropdownMenu>
      {showReactivationWarningModal && (
        <ReactivationWarningModal
          onClose={() => setShowReactivationWarningModal(false)}
        />
      )}
      {showDeactivationModal && (
        <DeactivatePackageModal
          packageName={packageName}
          onClose={() => setShowDeactivationModal(false)}
          onConfirm={() => submitDeactivate()}
        />
      )}
    </>
  );
}
